/**
 * The steps the reusable invite link (`/onboard/:advisorId`, AdvisorPublicInvite) shows when
 * the email entered already has an assessment with this advisor. The flow and its server calls
 * live in `usePublicInviteFlow`; these components only render one step each.
 *
 * - ReturningEmailChoice: continue / start over / someone else, asked before any form opens.
 * - ReturningSignIn: any choice while signed out. An email that already has an assessment always
 *   signs in first, because anyone can type an email into a public link. "Continue" then opens
 *   the profile list; "start over" / "someone else" open the new assessment's form.
 * - StartingNewAssessment: shown to a signed-in visitor while the browser moves into the new
 *   assessment's form.
 */
import { Lock, CheckCircle2 } from 'lucide-react';
import { ReturningIntakeChoice } from '@shared';
import { Button, ChoiceRadio, ChoiceRadioItem, Spinner } from '@design-system';

interface ReturningEmailChoiceProps {
  email: string;
  fullName: string;
  choice: ReturningIntakeChoice | null;
  isSubmitting: boolean;
  submitError: string | null;
  onChoiceChange: (choice: ReturningIntakeChoice) => void;
  onConfirm: () => void;
  onBack: () => void;
}

export function ReturningEmailChoice({
  email,
  fullName,
  choice,
  isSubmitting,
  submitError,
  onChoiceChange,
  onConfirm,
  onBack,
}: ReturningEmailChoiceProps) {
  const startsNewAssessment = !!choice && choice !== ReturningIntakeChoice.CONTINUE;

  return (
    <div className="space-y-6">
      <div className="space-y-2 text-center">
        <h2 className="text-lg font-medium text-gray-900">This email already has an assessment</h2>
        <p className="text-sm text-gray-700">
          <span className="font-semibold text-gray-900">{email}</span> has been used before. What
          would you like to do?
        </p>
      </div>

      <ChoiceRadio
        aria-label="What would you like to do?"
        value={choice ?? ''}
        onValueChange={value => onChoiceChange(value as ReturningIntakeChoice)}
      >
        <ChoiceRadioItem
          value={ReturningIntakeChoice.CONTINUE}
          title="Continue my assessment"
          description="Pick up where you left off, or see your results."
        />
        <ChoiceRadioItem
          value={ReturningIntakeChoice.START_OVER}
          title="Start over"
          description={`Begin a fresh assessment for ${fullName}. The earlier one stays saved in your account.`}
        />
        <ChoiceRadioItem
          value={ReturningIntakeChoice.SOMEONE_ELSE}
          title="Start one for someone else"
          description={`A separate assessment for ${fullName}, such as a spouse or parent who uses this email.`}
        />
      </ChoiceRadio>

      {submitError && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">{submitError}</div>
      )}

      <div className="space-y-2">
        <Button
          className="w-full rounded-full py-6 text-base font-medium"
          disabled={!choice || isSubmitting}
          onClick={onConfirm}
        >
          {isSubmitting ? (
            <>
              <Spinner className="size-5" />
              Processing...
            </>
          ) : startsNewAssessment ? (
            'Start new assessment'
          ) : (
            'Continue'
          )}
        </Button>
        <Button
          variant="ghost"
          className="text-primary-dark w-full rounded-full"
          disabled={isSubmitting}
          onClick={onBack}
        >
          Change name or email
        </Button>
      </div>
    </div>
  );
}

interface ReturningSignInProps {
  email: string;
  fullName: string;
  choice: ReturningIntakeChoice | null;
  signInUrl: string;
  onBack: () => void;
}

export function ReturningSignIn({ email, fullName, choice, signInUrl, onBack }: ReturningSignInProps) {
  const startsNewAssessment = !!choice && choice !== ReturningIntakeChoice.CONTINUE;

  return (
    <div className="space-y-6">
      <div className="bg-primary-100 mx-auto flex h-12 w-12 items-center justify-center rounded-full">
        <Lock className="text-primary-dark h-6 w-6" />
      </div>
      <div className="space-y-2 text-center">
        <h2 className="text-lg font-medium text-gray-900">
          {startsNewAssessment
            ? 'Sign in to start the new assessment'
            : 'Sign in to open your assessment'}
        </h2>
        <p className="text-sm text-gray-700">
          To keep your answers private, please sign in with{' '}
          <span className="font-semibold text-gray-900">{email}</span> first.
        </p>
        <p className="text-sm text-gray-700">
          {startsNewAssessment
            ? `The new assessment for ${fullName} opens as soon as you're signed in.`
            : "If more than one person uses this email, you'll choose whose assessment to open next."}
        </p>
      </div>
      <div className="space-y-2">
        <Button
          asChild
          className="w-full rounded-full py-6 text-base font-medium"
        >
          <a href={signInUrl}>Sign in</a>
        </Button>
        <Button
          variant="ghost"
          className="text-primary-dark w-full rounded-full"
          onClick={onBack}
        >
          Back
        </Button>
      </div>
    </div>
  );
}

interface StartingNewAssessmentProps {
  email: string;
  fullName: string;
  choice: ReturningIntakeChoice | null;
}

export function StartingNewAssessment({ email, fullName, choice }: StartingNewAssessmentProps) {
  return (
    <div className="flex flex-col items-center gap-4 py-2 text-center">
      <div className="bg-primary-100 flex h-14 w-14 items-center justify-center rounded-full">
        <CheckCircle2 className="text-primary-dark h-7 w-7" />
      </div>
      <h2 className="text-lg font-medium text-gray-900">
        Starting a new assessment for {fullName}
      </h2>
      <p className="max-w-sm text-sm text-gray-700">
        {choice === ReturningIntakeChoice.START_OVER
          ? 'Your earlier assessment stays saved in your account.'
          : `It's kept separate from the other assessment on ${email}.`}
      </p>
      <p className="text-primary-dark flex items-center gap-2 text-sm font-medium">
        <Spinner className="size-4" />
        Opening your questions…
      </p>
    </div>
  );
}
